import { useState, useEffect, useRef } from 'react';
import { X, Camera, Plus, UserMinus, Search, UserPlus, Send } from 'lucide-react';
import api, { uploadMedia } from '../lib/api';
import GroupMediaTab from './GroupMediaTab';

/**
 * GroupInfoSheet — full-screen Instagram-style sheet for managing a group.
 *
 * Props:
 *   group: { id, name, photo, kind ('general'|'plan'), creator_id, members[] }
 *   currentUserId
 *   onClose
 *   onUpdated(updatedGroup)  — called after successful edits / member changes
 */
export default function GroupInfoSheet({ group, currentUserId, onClose, onUpdated }) {
  const isCreator = group.creator_id === currentUserId;
  const isPlanChat = group.kind === 'plan';
  const [editingName, setEditingName] = useState(false);
  const [name, setName] = useState(group.name || '');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [confirmKick, setConfirmKick] = useState(null);
  const [showAddPicker, setShowAddPicker] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [tab, setTab] = useState('members'); // members | media
  const photoInputRef = useRef(null);

  useEffect(() => { setName(group.name || ''); }, [group.name]);

  const saveName = async () => {
    const v = name.trim();
    if (!v || saving || v === group.name) { setEditingName(false); return; }
    setSaving(true);
    try {
      const { data } = await api.patch(`/groups/${group.id}`, { name: v });
      onUpdated?.(data);
    } catch { setName(group.name || ''); }
    setSaving(false);
    setEditingName(false);
  };

  const onPhotoPicked = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadMedia(file);
      const { data } = await api.patch(`/groups/${group.id}`, { photo: res.url });
      onUpdated?.(data);
    } catch {/* ignore */}
    setUploading(false);
  };

  const kickMember = async (uid) => {
    try {
      await api.delete(`/groups/${group.id}/members/${uid}`);
      const { data } = await api.get(`/groups/${group.id}`);
      onUpdated?.(data);
    } catch {/* ignore */}
    setConfirmKick(null);
  };

  return (
    <div className="fixed inset-0 z-[10050] bg-black/40 flex items-end justify-center animate-fade-in" onClick={onClose} data-testid="group-info-overlay">
      <div className="w-full max-w-[480px] h-[88vh] bg-white rounded-t-3xl flex flex-col animate-slide-up" onClick={e => e.stopPropagation()} data-testid="group-info-sheet">
        <div className="flex-shrink-0 flex items-center justify-between px-4 py-3 border-b border-black/[0.04]">
          <p className="text-base font-bold text-[#111111]">Group info</p>
          <button onClick={onClose} data-testid="group-info-close" className="p-1 -mr-1 text-[#9B9EA3] active:scale-90"><X size={20} /></button>
        </div>

        <div className="flex-1 overflow-y-auto hide-scrollbar">
          {/* Avatar + name */}
          <div className="flex flex-col items-center pt-6 pb-5 px-4">
            <button
              type="button"
              onClick={() => isCreator && !isPlanChat && photoInputRef.current?.click()}
              disabled={!isCreator || isPlanChat || uploading}
              data-testid="group-photo-btn"
              className="relative w-24 h-24 rounded-full overflow-hidden bg-[#F1F0EA] flex items-center justify-center disabled:opacity-100"
            >
              {group.photo ? (
                <img loading="lazy" decoding="async" src={group.photo} alt="" className="w-full h-full object-cover" />
              ) : (
                <span className="text-xl font-bold text-[#9B9EA3]">{(group.name || '?').split(' ').map(s => s[0]).join('').slice(0, 2).toUpperCase()}</span>
              )}
              {isCreator && !isPlanChat && (
                <span className="absolute bottom-0 right-0 w-8 h-8 bg-[#B6E600] rounded-full flex items-center justify-center border-2 border-white">
                  <Camera size={14} className="text-[#111111]" />
                </span>
              )}
            </button>
            <input ref={photoInputRef} type="file" accept="image/*" className="hidden" onChange={onPhotoPicked} data-testid="group-photo-input" />

            <div className="mt-4 w-full text-center">
              {editingName && isCreator && !isPlanChat ? (
                <input
                  autoFocus
                  value={name}
                  onChange={e => setName(e.target.value)}
                  onBlur={saveName}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); saveName(); } if (e.key === 'Escape') { setName(group.name); setEditingName(false); } }}
                  maxLength={60}
                  data-testid="group-name-input"
                  className="text-lg font-bold text-center bg-[#F1F0EA] px-4 py-2 rounded-full focus:outline-none w-full max-w-[280px]"
                />
              ) : (
                <button
                  type="button"
                  onClick={() => isCreator && !isPlanChat && setEditingName(true)}
                  disabled={!isCreator || isPlanChat}
                  data-testid="group-name-btn"
                  className="text-lg font-bold text-[#111111] disabled:opacity-100"
                >
                  {group.name}
                </button>
              )}
              <p className="text-xs text-[#9B9EA3] mt-1">
                {isPlanChat ? 'Plan group chat · members sync from plan' : `${group.member_count || group.members.length} members`}
              </p>
            </div>
          </div>

          {/* Tabs */}
          <div className="px-4">
            <div className="flex gap-1 bg-[#F1F0EA] rounded-full p-1" data-testid="group-info-tabs">
              <button
                onClick={() => setTab('members')}
                data-testid="group-info-tab-members"
                className={`flex-1 py-2 rounded-full text-xs font-bold transition-colors ${tab === 'members' ? 'bg-white text-[#111111] shadow-sm' : 'text-[#6B6E73]'}`}
              >
                Members
              </button>
              <button
                onClick={() => setTab('media')}
                data-testid="group-info-tab-media"
                className={`flex-1 py-2 rounded-full text-xs font-bold transition-colors ${tab === 'media' ? 'bg-white text-[#111111] shadow-sm' : 'text-[#6B6E73]'}`}
              >
                Media
              </button>
            </div>
          </div>

          {/* Members tab */}
          {tab === 'members' && (
          <div className="px-4 mt-3">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-bold text-[#9B9EA3] uppercase tracking-wider">Members</p>
              {isCreator && !isPlanChat && (
                <button onClick={() => setShowAddPicker(true)} data-testid="group-add-member-btn" className="flex items-center gap-1 text-xs font-semibold text-[#3F5200] active:scale-95">
                  <Plus size={14} /> Add
                </button>
              )}
              {isPlanChat && group.plan_id && (
                <button onClick={() => setShowInvite(true)} data-testid="group-invite-friends-btn" className="flex items-center gap-1 text-xs font-semibold text-[#3F5200] active:scale-95">
                  <UserPlus size={14} /> Invite friends
                </button>
              )}
            </div>
            <div className="space-y-1">
              {group.members.map(m => (
                <div key={m.id} className="flex items-center gap-3 p-2.5 rounded-2xl" data-testid={`group-member-${m.id}`}>
                  {m.profile_photo ? (
                    <img loading="lazy" decoding="async" src={m.profile_photo} alt="" className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-[#F1F0EA] flex items-center justify-center text-xs font-bold text-[#9B9EA3] flex-shrink-0">{(m.display_name || '?').split(' ').map(s => s[0]).join('').slice(0, 2).toUpperCase()}</div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-[#111111] truncate">{m.display_name}{m.id === group.creator_id && <span className="ml-1.5 text-[10px] text-[#6B8A00] font-bold">· Admin</span>}</p>
                    <p className="text-[11px] text-[#9B9EA3] truncate">@{m.username}</p>
                  </div>
                  {isCreator && !isPlanChat && m.id !== currentUserId && (
                    <button onClick={() => setConfirmKick(m)} data-testid={`group-kick-${m.id}`} className="p-2 text-[#FF3B30] active:scale-90"><UserMinus size={16} /></button>
                  )}
                </div>
              ))}
            </div>
          </div>
          )}

          {/* Media tab */}
          {tab === 'media' && (
            <div className="px-4 mt-3" data-testid="group-info-media-pane">
              <GroupMediaTab groupId={group.id} currentUserId={currentUserId} />
            </div>
          )}

          <div className="h-12" />
        </div>
      </div>

      {/* Kick confirmation */}
      {confirmKick && (
        <div className="fixed inset-0 z-[10060] flex items-center justify-center px-6 bg-black/50 backdrop-blur-[2px] animate-fade-in" onClick={() => setConfirmKick(null)}>
          <div className="w-full max-w-[340px] bg-white rounded-3xl p-6 animate-scale-in" onClick={e => e.stopPropagation()} data-testid="group-kick-confirm">
            <h3 className="text-base font-bold text-[#111111] mb-1">Remove {confirmKick.display_name}?</h3>
            <p className="text-sm text-[#6B6E73] mb-5">They won't see new messages from this group.</p>
            <button onClick={() => kickMember(confirmKick.id)} data-testid="group-kick-confirm-btn" className="w-full bg-[#FF3B30] text-white font-bold rounded-full text-sm py-3 mb-2 active:scale-[0.98]">Remove</button>
            <button onClick={() => setConfirmKick(null)} className="w-full text-[#111111] font-semibold rounded-full text-sm py-3">Cancel</button>
          </div>
        </div>
      )}

      {/* Add member picker */}
      {showAddPicker && (
        <AddMemberPicker
          group={group}
          onClose={() => setShowAddPicker(false)}
          onAdded={async () => {
            const { data } = await api.get(`/groups/${group.id}`);
            onUpdated?.(data);
            setShowAddPicker(false);
          }}
        />
      )}

      {/* Invite friends (plan chats only) */}
      {showInvite && group.plan_id && (
        <InvitePlanFriends
          planId={group.plan_id}
          existingIds={new Set(group.members.map(m => m.id))}
          onClose={() => setShowInvite(false)}
        />
      )}
    </div>
  );
}


function AddMemberPicker({ group, onClose, onAdded }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const existingIds = new Set(group.members.map(m => m.id));

  useEffect(() => {
    if (!q.trim()) { setResults([]); return; }
    let alive = true;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/explore/people?q=${encodeURIComponent(q.trim())}`);
        if (alive) setResults((data || []).filter(u => !existingIds.has(u.id)));
      } catch {/* ignore */}
      if (alive) setLoading(false);
    }, 220);
    return () => { alive = false; clearTimeout(t); };
  }, [q]);

  const toggle = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const submit = async () => {
    if (selected.size === 0 || saving) return;
    setSaving(true);
    try {
      await api.post(`/groups/${group.id}/members`, { member_ids: Array.from(selected) });
      await onAdded?.();
    } catch {/* ignore */}
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[10070] bg-black/40 flex items-end justify-center animate-fade-in" onClick={onClose} data-testid="add-member-overlay">
      <div className="w-full max-w-[480px] h-[80vh] bg-white rounded-t-3xl flex flex-col animate-slide-up" onClick={e => e.stopPropagation()} data-testid="add-member-sheet">
        <div className="flex-shrink-0 flex items-center justify-between px-4 py-3 border-b border-black/[0.04]">
          <button onClick={onClose} className="text-sm font-semibold text-[#9B9EA3]">Cancel</button>
          <p className="text-base font-bold text-[#111111]">Add members</p>
          <button disabled={selected.size === 0 || saving} onClick={submit} data-testid="add-member-confirm" className="text-sm font-bold text-[#3F5200] disabled:opacity-40">{saving ? 'Adding…' : `Add (${selected.size})`}</button>
        </div>
        <div className="flex-shrink-0 px-4 py-2">
          <div className="bg-[#F1F0EA] rounded-full flex items-center gap-2 px-3.5 py-2.5">
            <Search size={16} className="text-[#9B9EA3]" />
            <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="Search by username or name" data-testid="add-member-search" className="flex-1 bg-transparent text-sm focus:outline-none placeholder-[#B0B3B8]" />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto hide-scrollbar px-2">
          {loading && <p className="text-center text-xs text-[#9B9EA3] py-4">Searching…</p>}
          {!loading && q && results.length === 0 && <p className="text-center text-xs text-[#9B9EA3] py-6">No new users match.</p>}
          {results.map(u => {
            const on = selected.has(u.id);
            return (
              <button key={u.id} onClick={() => toggle(u.id)} data-testid={`add-member-row-${u.id}`} className={`w-full flex items-center gap-3 p-2.5 rounded-2xl text-left transition-colors ${on ? 'bg-[#B6E600]/15' : 'active:bg-[#F1F0EA]'}`}>
                {u.profile_photo ? <img loading="lazy" decoding="async" src={u.profile_photo} alt="" className="w-10 h-10 rounded-full object-cover" /> : <div className="w-10 h-10 rounded-full bg-[#F1F0EA] flex items-center justify-center text-xs font-bold text-[#9B9EA3]">{(u.display_name || '?').split(' ').map(s => s[0]).join('').slice(0, 2).toUpperCase()}</div>}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-[#111111] truncate">{u.display_name}</p>
                  <p className="text-[11px] text-[#9B9EA3] truncate">@{u.username}</p>
                </div>
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${on ? 'bg-[#B6E600] border-[#B6E600]' : 'border-[#D5D5CE]'}`}>
                  {on && <span className="w-2 h-2 rounded-full bg-[#111111]" />}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}



function InvitePlanFriends({ planId, existingIds, onClose }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(0);

  useEffect(() => {
    if (!q.trim()) { setResults([]); return; }
    let alive = true;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/explore/people?q=${encodeURIComponent(q.trim())}`);
        if (alive) setResults((data || []).filter(u => !existingIds.has(u.id)));
      } catch {/* ignore */}
      if (alive) setLoading(false);
    }, 220);
    return () => { alive = false; clearTimeout(t); };
  }, [q, existingIds]);

  const toggle = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const submit = async () => {
    if (selected.size === 0 || sending) return;
    setSending(true);
    try {
      const { data } = await api.post(`/plans/${planId}/invite`, { user_ids: Array.from(selected) });
      setSent(data?.sent || 0);
      setTimeout(() => onClose(), 1100);
    } catch {/* ignore */}
    setSending(false);
  };

  return (
    <div className="fixed inset-0 z-[10076] bg-black/40 flex items-end justify-center animate-fade-in" onClick={onClose} data-testid="invite-friends-overlay">
      <div className="w-full max-w-[480px] h-[80vh] bg-white rounded-t-3xl flex flex-col animate-slide-up" onClick={e => e.stopPropagation()} data-testid="invite-friends-sheet">
        <div className="flex-shrink-0 flex items-center justify-between px-4 py-3 border-b border-black/[0.04]">
          <button onClick={onClose} className="text-sm font-semibold text-[#9B9EA3]">Cancel</button>
          <p className="text-base font-bold text-[#111111]">Invite friends</p>
          <button
            disabled={selected.size === 0 || sending}
            onClick={submit}
            data-testid="invite-friends-confirm"
            className="text-sm font-bold text-[#3F5200] disabled:opacity-40 flex items-center gap-1"
          >
            <Send size={14} /> {sending ? 'Sending…' : `Invite (${selected.size})`}
          </button>
        </div>
        <div className="flex-shrink-0 px-4 py-2">
          <div className="bg-[#F1F0EA] rounded-full flex items-center gap-2 px-3.5 py-2.5">
            <Search size={16} className="text-[#9B9EA3]" />
            <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="Search by username or name" data-testid="invite-friends-search" className="flex-1 bg-transparent text-sm focus:outline-none placeholder-[#B0B3B8]" />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto hide-scrollbar px-2">
          {sent > 0 && (
            <p className="text-center text-xs text-[#3F5200] py-3 font-bold" data-testid="invite-friends-success">
              ✅ {sent} invite{sent === 1 ? '' : 's'} sent
            </p>
          )}
          {loading && <p className="text-center text-xs text-[#9B9EA3] py-4">Searching…</p>}
          {!loading && q && results.length === 0 && <p className="text-center text-xs text-[#9B9EA3] py-6">No users match.</p>}
          {results.map(u => {
            const on = selected.has(u.id);
            return (
              <button key={u.id} onClick={() => toggle(u.id)} data-testid={`invite-friend-row-${u.id}`} className={`w-full flex items-center gap-3 p-2.5 rounded-2xl text-left transition-colors ${on ? 'bg-[#B6E600]/15' : 'active:bg-[#F1F0EA]'}`}>
                {u.profile_photo ? <img loading="lazy" decoding="async" src={u.profile_photo} alt="" className="w-10 h-10 rounded-full object-cover" /> : <div className="w-10 h-10 rounded-full bg-[#F1F0EA] flex items-center justify-center text-xs font-bold text-[#9B9EA3]">{(u.display_name || '?').split(' ').map(s => s[0]).join('').slice(0, 2).toUpperCase()}</div>}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-[#111111] truncate">{u.display_name}</p>
                  <p className="text-[11px] text-[#9B9EA3] truncate">@{u.username}</p>
                </div>
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${on ? 'bg-[#B6E600] border-[#B6E600]' : 'border-[#D5D5CE]'}`}>
                  {on && <span className="w-2 h-2 rounded-full bg-[#111111]" />}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
